import React, { memo, useCallback, useMemo } from 'react'
import {
  Table as BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/Table'
import Text from '../ui/Text'
import ResourceCell from './ResourceCell'
import EmptyState from '../ui/EmptyState'
import Loader from '../shared/Loader'
import Pagination, { PER_PAGE_OPTIONS } from '../ui/pagination'
import ProgressionService from '../../services/api/ProgressionService'
import { formatDateTime } from '../../utils/dateHelpers'
import Card from '../ui/Card'
import { PROGRESSION_TABLE_CLASS, PROGRESSION_TABLE_HEAD_CLASS } from './constants'
import { usePaginatedTable } from './hooks'

const EMPTY_MESSAGE = 'Le journal d\'activité apparaîtra ici une fois les données disponibles.'

const OperatorsActivityLogTable = ({ collectId, dateRange, draftId, operatorId, resourceSearch }) => {
  const fetcher = useCallback(({ page, perPage, sortBy, sortDirection }) => ProgressionService
    .fetchList(collectId, draftId, {
      name: 'activity_log',
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      operatorId,
      resourceSearch,
      page,
      perPage,
      sortBy,
      sortDirection
    }), [collectId, draftId, dateRange.startDate, dateRange.endDate, operatorId, resourceSearch])

  const filtersKey = useMemo(() => JSON.stringify({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    operatorId,
    resourceSearch
  }), [dateRange.startDate, dateRange.endDate, operatorId, resourceSearch])

  const {
    rows, isLoading, sortProps,
    page, perPage, total, lastPage, setPage, setPerPage
  } = usePaginatedTable({ fetcher, rowsKey: 'rows', filtersKey })

  const isEmpty = rows.length === 0

  const renderBody = () => {
    if (isEmpty) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            {isLoading ? <div className="tw:h-52" /> : <EmptyState message={EMPTY_MESSAGE} />}
          </TableCell>
        </TableRow>
      )
    }
    return rows.map(entry => (
      <TableRow key={entry.id}>
        <TableCell>
          <ResourceCell
            collectId={collectId}
            draftId={draftId || entry.draft_id}
            name={entry.resource_name}
            resourceId={entry.resource_id}
          />
        </TableCell>
        <TableCell><Text lines={2}>{entry.section_name || '-'}</Text></TableCell>
        <TableCell><Text lines={2}>{entry.user_name || '-'}</Text></TableCell>
        <TableCell>{formatDateTime(entry.updated_at)}</TableCell>
      </TableRow>
    ))
  }

  return (
    <Card title="Journal d'activité" plainHeader>
      <div className="tw:relative tw:overflow-x-auto">
        <BaseTable className={PROGRESSION_TABLE_CLASS} hover={!isEmpty} bordered>
          <TableHead className={PROGRESSION_TABLE_HEAD_CLASS}>
            <TableRow>
              <TableHeader className="tw:w-[30%]" {...sortProps('resource_name')}>
                Ressource
              </TableHeader>
              <TableHeader className="tw:w-[20%]" {...sortProps('section_name')}>
                Section
              </TableHeader>
              <TableHeader className="tw:w-[24%]" {...sortProps('user_name')}>
                Opérateur
              </TableHeader>
              <TableHeader className="tw:w-[26%]" {...sortProps('updated_at')}>
                Date
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </BaseTable>
        {isLoading ? (
          <div
            className={'tw:absolute tw:inset-x-0 tw:bottom-0 tw:top-12 tw:flex tw:items-center '
              + 'tw:justify-center tw:bg-white/70 tw:z-10'}
          >
            <Loader />
          </div>
        ) : null}
      </div>
      {isEmpty ? null : (
        <Pagination
          disabled={isLoading}
          lastPage={lastPage}
          page={page}
          perPage={perPage}
          perPageOptions={PER_PAGE_OPTIONS}
          total={total}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </Card>
  )
}

export default memo(OperatorsActivityLogTable)
